import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- App origin for in-email links (conversation page, genie page, etc.) ---
const _resolvedOrigin = "https://helpgenie.ai";

const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (mirrors src/types/email-templates.ts) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface ConversationDigestConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

interface DigestConversationSummary {
  id: string;
  createdAt: string;
  summary?: string | null;
  sentimentScore?: number | null;
  needsAttention?: boolean;
}

interface ConversationDigestData {
  agentId?: string;
  agentName: string;
  agentDescription?: string | null;
  /** GenieBranding jsonb — used by the Email Signature block. */
  agentBranding?: Record<string, any> | null;
  periodStart: string;
  periodEnd: string;
  conversationCount: number;
  conversations: DigestConversationSummary[];
  topics?: string[];
  sentimentBreakdown?: { positive: number; neutral: number; negative: number };
  recommendations?: { text: string; actionType: string | null }[];
  escalations?: DigestConversationSummary[];
  timezone?: string;
}

const DEFAULT_LABELS: Record<string, string> = {
  digest_summary: "Period Summary",
  digest_sentiment_breakdown: "Sentiment Breakdown",
  digest_topics: "Top Topics",
  digest_recommendations: "Recommendations",
  digest_conversation_list: "Conversations This Period",
  digest_escalations: "Escalations This Period",
  mobile_app_promo: "Help Genie in your pocket",
};

// Maps a recommendation's `actionType` (a `?section=` anchor id on the Genie
// detail page) to the button label shown in the digest email.
const SECTION_ACTION_LABELS: Record<string, string> = {
  "section-distribution": "Update deployment",
  "section-identity": "Update persona/voice",
  "section-prompts": "Update prompt/greeting",
  "section-knowledge": "Add to Knowledge Base",
  "section-documents": "Update documents",
  "section-branding": "Update branding",
  "section-mailbox": "Update mailbox/report settings",
  "section-tools": "Update integrations",
  "section-intelligence": "Update model settings",
  "section-extra": "Update settings",
};

function renderInlineButton(text: string, url: string, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  return `<a href="${url}" style="display:inline-block;padding:6px 14px;background-color:${colors.primary};color:#FFFFFF;text-decoration:none;font-weight:600;font-size:11px;border-radius:${metricButtonRadius};font-family:${fontFamily};box-shadow:${styles.shadowEnabled ? `0 2px 8px ${brandHexToRgba(colors.primary, 0.25)}` : "none"};">${text}</a>`;
}

function brandHexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveBlocks(config: ConversationDigestConfig | null | undefined): ContentBlock[] {
  // Signature depends on genie branding a tenant may not have configured, so
  // it starts OFF when backfilled — unlike the rest of the value layer.
  // digest_recommendations is a newly added block — backfilled ON so saved
  // configs from before it existed still pick it up.
  const VALUE_LAYER_AUTOINJECT: string[] = ["signature", "digest_recommendations"];
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  const defaults: ContentBlock[] = [
    { id: "digest_summary", type: "digest_summary", enabled: true, order: 0 },
    { id: "digest_sentiment_breakdown", type: "digest_sentiment_breakdown", enabled: true, order: 1 },
    { id: "digest_topics", type: "digest_topics", enabled: true, order: 2 },
    { id: "digest_recommendations", type: "digest_recommendations", enabled: true, order: 3 },
    { id: "digest_escalations", type: "digest_escalations", enabled: true, order: 4 },
    { id: "digest_conversation_list", type: "digest_conversation_list", enabled: true, order: 5 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 6 },
    { id: "signature", type: "signature", enabled: false, order: 7 },
  ];

  const savedBlocks = ownSavedBlocks(config, defaults);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, defaults);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const type of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(type)) continue;
    maxOrder += 1;
    blocks.push({ id: type, type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(type), order: maxOrder });
    existing.add(type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  metricButtonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
  buttonText: string;
}

function renderDigestSummary(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_summary;
  const headerColor = colors.headerColor;

  const cell = (value: string, cellLabel: string, accent: string) => `
    <td width="50%" style="padding: 3px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: ${accent}0D; border: 1px solid ${accent}40; border-radius: 6px;">
        <tr>
          <td style="padding: 9px 11px;">
            <div style="font-size: 19px; font-weight: 800; color: ${accent}; line-height: 1; font-family: 'Baloo Paaji 2', ${fontFamily};">${value}</div>
            <div style="font-size: 9px; color: #6B7280; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px;">${cellLabel}</div>
          </td>
        </tr>
      </table>
    </td>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size:11px;color:#6B7280;font-family:${fontFamily};margin-bottom:10px;">${formatDateRange(data.periodStart, data.periodEnd, data.timezone)}</div>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${cell(String(data.conversationCount), "Conversations", colors.primary)}
                  ${cell(String(data.escalations?.length ?? 0), "Escalations", colors.accent)}
                </tr>
              </table>
              ${
                data.agentId
                  ? `
              <table cellpadding="0" cellspacing="0" border="0" style="margin-top:12px;">
                <tr>
                  <td>
                    <a href="${_resolvedOrigin}/genie-hub/inbox?tab=conversations" style="display:inline-block;padding:8px 18px;background-color:${colors.primary};color:#FFFFFF;text-decoration:none;font-weight:600;font-size:12px;border-radius:${metricButtonRadius};font-family:${fontFamily};">${styles.buttonText}</a>
                  </td>
                </tr>
              </table>`
                  : ""
              }
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSentimentBreakdown(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const breakdown = data.sentimentBreakdown;
  if (!breakdown) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_sentiment_breakdown;
  const total = breakdown.positive + breakdown.neutral + breakdown.negative || 1;

  const bar = (value: number, color: string, name: string) => `
    <tr>
      <td style="padding: 3px 0; font-size: 12px; color: ${colors.text}; font-family: ${fontFamily};">
        <div style="display:flex;justify-content:space-between;margin-bottom:2px;"><span>${name}</span><span>${value}</span></div>
        <div style="background:#E5EAF0;border-radius:4px;height:6px;overflow:hidden;">
          <div style="background:${color};height:6px;width:${Math.round((value / total) * 100)}%;"></div>
        </div>
      </td>
    </tr>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                ${bar(breakdown.positive, "#22C55E", "Positive")}
                ${bar(breakdown.neutral, "#9CA3AF", "Neutral")}
                ${bar(breakdown.negative, "#EF4444", "Negative")}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDigestRecommendations(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const recommendations = data.recommendations || [];
  if (recommendations.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_recommendations;

  const items = recommendations
    .map((r) => {
      const actionLabel = r.actionType ? SECTION_ACTION_LABELS[r.actionType] : undefined;
      const button =
        actionLabel && data.agentId
          ? `<div style="margin-top:6px;">${renderInlineButton(actionLabel, `${_resolvedOrigin}/genie-hub/my-genies/${data.agentId}?section=${r.actionType}`, styles)}</div>`
          : "";
      return `
        <tr>
          <td style="padding: 4px 0 10px 0; font-size: 12px; color: ${colors.text}; font-family: ${fontFamily}; line-height: 1.4;">
            &bull;&nbsp; ${r.text}
            ${button}
          </td>
        </tr>`;
    })
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${brandHexToRgba(colors.accent, 0.05)};border:1px solid ${brandHexToRgba(colors.accent, 0.3)};border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.accent};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${items}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDigestTopics(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const topics = data.topics || [];
  if (topics.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_topics;

  const chips = topics
    .map(
      (topic) =>
        `<table cellpadding="0" cellspacing="0" border="0" style="display: inline-table; margin: 2px;"><tr><td style="padding: 5px 11px; background-color: ${brandHexToRgba(colors.primary, 0.08)}; color: ${colors.primary}; font-size: 12px; font-weight: 500; border: 1px solid ${brandHexToRgba(colors.primary, 0.22)}; border-radius: 999px; font-family: ${fontFamily};">${topic}</td></tr></table>`,
    )
    .join(" ");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="5" border="0" width="100%"><tr><td>${chips}</td></tr></table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDigestEscalations(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const escalations = data.escalations || [];
  if (escalations.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_escalations;

  const rows = escalations
    .map((c) => {
      const conversationUrl = `${_resolvedOrigin}/genie-hub/conversations/${c.id}`;
      const replyLink = `<a href="mailto:?subject=${encodeURIComponent(`Re: ${data.agentName} conversation`)}&body=${encodeURIComponent(`\n\n---\n${c.summary || "No summary available"}\n\n${conversationUrl}`)}" style="font-size:11px;color:${colors.primary};text-decoration:none;font-family:${fontFamily};margin-left:10px;">Reply to customer &rarr;</a>`;
      return `
        <tr>
          <td style="padding-bottom:6px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${brandHexToRgba("#DC2626", 0.05)};border-left:3px solid #DC2626;border-radius:6px;">
              <tr>
                <td style="padding:9px 12px;">
                  <div style="font-size:11px;color:#6B7280;font-family:${fontFamily};margin-bottom:2px;">${new Date(c.createdAt).toLocaleString()}</div>
                  <div style="font-size:12px;color:${colors.text};font-family:${fontFamily};line-height:1.4;">${c.summary || "No summary available"}</div>
                  <a href="${conversationUrl}" style="font-size:11px;color:${colors.primary};text-decoration:none;font-family:${fontFamily};">View conversation &rarr;</a>${replyLink}
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
    })
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDigestConversationList(block: ContentBlock, data: ConversationDigestData, styles: StyleContext): string {
  const conversations = data.conversations || [];
  if (conversations.length === 0) return "";
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.digest_conversation_list;

  const rows = conversations
    .slice(0, 25)
    .map(
      (c) => `
        <tr>
          <td style="padding: 6px 0; border-bottom: 1px solid #E5EAF0; font-size: 12px; color: ${colors.text}; font-family: ${fontFamily};">
            <a href="${_resolvedOrigin}/genie-hub/conversations/${c.id}" style="color:${colors.primary};text-decoration:none;font-weight:600;">${new Date(c.createdAt).toLocaleString()}</a>
            <div style="color:#6B7280;margin-top:2px;">${c.summary || "No summary available"}</div>
          </td>
        </tr>`,
    )
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${colors.headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderMobileAppPromo(block: ContentBlock, _data: ConversationDigestData, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, metricButtonRadius } = styles;
  const heading = settings.heading || "Share Help Genie with your customers";
  const body = settings.body || "Get your team and customers on the app for instant support on every product.";
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  const headerColor = colors.headerColor || colors.text || "#28406F";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
              <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
              <table cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="padding-right: 8px;">
                    <a href="${appStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                  <td>
                    <a href="${playStoreUrl}" style="display: inline-block;">
                      <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function formatDateRange(start: string, end: string, timezone = "UTC"): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, month: "short", day: "numeric", year: "numeric" });
    return `${formatter.format(new Date(start))} — ${formatter.format(new Date(end))}`;
  } catch {
    return `${start} — ${end}`;
  }
}

function renderSignature(_block: ContentBlock, data: ConversationDigestData, _styles: StyleContext): string {
  const html = renderSignatureBlockHtml(data.agentName || "Your Genie", data.agentDescription, data.agentBranding);
  if (!html) return "";
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

const blockRenderers: Record<
  string,
  (block: ContentBlock, data: ConversationDigestData, styles: StyleContext) => string
> = {
  digest_summary: renderDigestSummary,
  digest_sentiment_breakdown: renderSentimentBreakdown,
  digest_topics: renderDigestTopics,
  digest_recommendations: renderDigestRecommendations,
  digest_escalations: renderDigestEscalations,
  digest_conversation_list: renderDigestConversationList,
  mobile_app_promo: renderMobileAppPromo,
  signature: renderSignature,
};

export function generateConversationDigestEmailConfigurable(
  data: ConversationDigestData,
  config?: ConversationDigestConfig | null,
): string {
  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: undefined as string | undefined,
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
      buttonText: "View All Conversations",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  const headerTextColor = colors.headerColor || colors.text || "#28406F";
  const boxShadow = buttons.shadowEnabled ? `0 4px 20px ${brandHexToRgba(colors.primary || defaults.colors.primary, 0.1)}` : "none";
  const metricButtonRadius = getButtonRadius();

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    metricButtonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
    buttonText: buttons.buttonText || defaults.buttons.buttonText,
  };

  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  const blocksHtml = enabledBlocks
    .map((block) => {
      const renderer = blockRenderers[block.type];
      if (!renderer) return "";
      return renderer(block, data, styleContext);
    })
    .filter(Boolean)
    .join("\n");

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && data.agentBranding?.signatureStyle?.enabled,
  );

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Conversation Digest</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">
              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${boxShadow}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">${data.agentName} — Conversation Digest</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                ${blocksHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
}
